import React from 'react';
import { Switch, Redirect, Route, useHistory } from 'react-router-dom';
import PrivateRoute from 'components/Router.PrivateRoute';
import {
  DASHBOARD,
  HOME_DASHBOARD,
  LOGIN,
  CAMPAIGN_LIST,
  CAMPAIGN_CREATE,
  CAMPAIGN_SHOW,
  CAMPAIGN_EDIT,
  CAMPAIGN_CODES_ASSIGN,
  CLIENT_LIST,
  CLIENT_CREATE,
  CLIENT_EDIT,
  PROPOSAL_LIST,
  PROPOSAL_SHOW,
  PROPOSAL_EDIT,
  PROPOSAL_CREATE,
  PROPOSAL_COMMITTED,
  PRODUCT_LIST,
  PRODUCT_CREATE,
  PRODUCT_EDIT,
  //MARKET_LIST,
  PROPOSAL_NEGOTIATION,
  USER_LIST,
  USER_SETTINGS,
  AD_FORMAT_LIST,
  AD_FORMAT_CREATE,
  AD_FORMAT_EDIT,
  AD_SLOT_LIST,
  AD_SLOT_CREATE,
  AD_SLOT_EDIT,
  AD_TYPE_LIST,
  MARKET_SHOW,
  PROPOSAL_CREATE_FROM_CAMPAIGN,
  LOGIN_CALLBACK,
  TRADING_LIST,
  CLIENT_MANAGER_LIST,
  CLIENT_MANAGER_CREATE,
  CLIENT_MANAGER_EDIT,
  PROPOSAL_PRODUCT_COUNTEROFFER_CAT,
} from 'routes';
import {
  CAN_VIEW_CAMPAIGNS,
  CAN_EDIT_CAMPAIGNS,
  CAN_VIEW_CLIENTS,
  CAN_EDIT_CLIENTS,
  CAN_VIEW_PROPOSALS,
  CAN_EDIT_PRODUCTS,
  CAN_VIEW_USERS,
  //CAN_EDIT_USERS,
  CAN_EDIT_PRODUCT_METADATA,
  CAN_VIEW_CLIENT_MANAGER,
  CAN_EDIT_CLIENT_MANAGER,
  CAN_GENERATE_TRADING_REPORT,
} from 'consts/permissions';
import { LoginCallback, Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import NotFound from 'pages/NotFound';

const KitchenSink = React.lazy(() => import('pages/KitchenSink'));
const Login = React.lazy(() => import('pages/Login'));

const CampaignList = React.lazy(() => import('pages/Campaign/Campaign.List'));
const CampaignShow = React.lazy(() => import('pages/Campaign/Campaign.Show'));
const CampaignCreate = React.lazy(() =>
  import('pages/Campaign/Campaign.Create'),
);
const CampaignEdit = React.lazy(() => import('pages/Campaign/Campaign.Edit'));
const CampaignCodesAssign = React.lazy(() =>
  import('pages/Campaign/Campaign.CodesAssign'),
);

const ClientList = React.lazy(() => import('pages/Client/Client.List'));
const ClientCreate = React.lazy(() => import('pages/Client/Client.Create'));
const ClientEdit = React.lazy(() => import('pages/Client/Client.Edit'));
// const ClientShow = React.lazy(() => import('pages/Client/Client.Show'));

const ProposalList = React.lazy(() => import('pages/Proposal/Proposal.List'));
const ProposalShow = React.lazy(() =>
  import('pages/Proposal/Proposal.Negotiation'),
);
const ProposalEdit = React.lazy(() =>
  import('pages/Proposal/Proposal.Negotiation'),
);
const ProposalNegotiation = React.lazy(() =>
  import('pages/Proposal/Proposal.Negotiation'),
);
const ProposalCreate = React.lazy(() =>
  import('pages/Proposal/Proposal.Create'),
);
const ProposalCommitted = React.lazy(() =>
  import('pages/Proposal/Proposal.Committed'),
);
const ProposalCounterOffer = React.lazy(() =>
  import('pages/Proposal/Proposal.CounterOffer'),
);

const ProductList = React.lazy(() => import('pages/Product/Product.List'));
const ProductCreate = React.lazy(() => import('pages/Product/Product.Create'));
const ProductEdit = React.lazy(() => import('pages/Product/Product.Edit'));

const MarketList = React.lazy(() => import('pages/Market/Market.List'));

const UserList = React.lazy(() => import('pages/User/User.List'));
const UserSettings = React.lazy(() => import('pages/User/User.Settings'));

const AdFormatList = React.lazy(() => import('pages/AdFormat/AdFormat.List'));
const AdFormatCreate = React.lazy(() =>
  import('pages/AdFormat/AdFormat.Create'),
);
const AdFormatEdit = React.lazy(() => import('pages/AdFormat/AdFormat.Edit'));

const AdSlotList = React.lazy(() => import('pages/AdSlot/AdSlot.List'));
const AdSlotCreate = React.lazy(() => import('pages/AdSlot/AdSlot.Create'));
const AdSlotEdit = React.lazy(() => import('pages/AdSlot/AdSlot.Edit'));

const AdTypeList = React.lazy(() => import('pages/AdType/AdType.List'));

const Trading = React.lazy(() => import('pages/Trading/Trading.List'));

const ClientManagerList = React.lazy(() =>
  import('pages/ClientManager/ClientManager.List'),
);
const ClientManagerCreate = React.lazy(() =>
  import('pages/ClientManager/ClientManager.Create'),
);
const ClientManagerEdit = React.lazy(() =>
  import('pages/ClientManager/ClientManager.Edit'),
);

const HomeDashboard = React.lazy(() => import('pages/Home/Home.Dashboard'));

const Router = () => {
  const history = useHistory();
  const oktaAuth = new OktaAuth({
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: window.location.origin + '/login/callback',
  });
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(
      toRelativeUrl(originalUri || HOME_DASHBOARD, window.location.origin),
    );
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Switch>
        <Route path={LOGIN} exact component={Login} />
        <Route path={LOGIN_CALLBACK} exact component={LoginCallback} />

        <Route path='/kitchensink' exact component={KitchenSink} />

        <Redirect from={DASHBOARD} exact to={HOME_DASHBOARD} />

        <PrivateRoute
          path={HOME_DASHBOARD}
          exact
          component={HomeDashboard}
          permissions={[CAN_VIEW_CAMPAIGNS]}
        />
        {/* kampanie */}
        <PrivateRoute
          path={CAMPAIGN_LIST}
          exact
          component={CampaignList}
          permissions={[CAN_VIEW_CAMPAIGNS]}
        />
        <PrivateRoute
          path={CAMPAIGN_CREATE}
          exact
          component={CampaignCreate}
          permissions={[CAN_EDIT_CAMPAIGNS]}
        />
        <PrivateRoute
          path={CAMPAIGN_SHOW}
          exact
          component={CampaignShow}
          permissions={[CAN_VIEW_CAMPAIGNS]}
        />
        <PrivateRoute
          path={CAMPAIGN_EDIT}
          component={CampaignEdit}
          permissions={[CAN_VIEW_CAMPAIGNS]}
        />
        <PrivateRoute
          path={CAMPAIGN_CODES_ASSIGN}
          exact
          component={CampaignCodesAssign}
          permissions={[CAN_VIEW_CAMPAIGNS]}
        />

        {/* reklamodawcy */}
        <PrivateRoute
          path={CLIENT_LIST}
          exact
          component={ClientList}
          permissions={[CAN_VIEW_CLIENTS]}
        />
        <PrivateRoute
          path={CLIENT_CREATE}
          exact
          component={ClientCreate}
          permissions={[CAN_EDIT_CLIENTS]}
        />
        <PrivateRoute
          path={CLIENT_EDIT}
          component={ClientEdit}
          permissions={[CAN_VIEW_CLIENTS]}
        />
        {/* <PrivateRoute
        path={CLIENT_SHOW}
        component={ClientShow}
        permissions={[CAN_VIEW_CLIENTS]}
      /> */}

        {/* proposals */}
        <PrivateRoute
          path={PROPOSAL_LIST}
          exact
          component={ProposalList}
          permissions={[CAN_VIEW_PROPOSALS]}
        />
        <PrivateRoute
          path={PROPOSAL_SHOW}
          component={ProposalShow}
          exact
          permissions={[CAN_VIEW_PROPOSALS]}
        />
        <PrivateRoute
          path={PROPOSAL_EDIT}
          component={ProposalEdit}
          exact
          permissions={[CAN_VIEW_PROPOSALS]}
        />
        <PrivateRoute
          path={PROPOSAL_NEGOTIATION}
          component={ProposalNegotiation}
          exact
          permissions={[CAN_VIEW_PROPOSALS]}
        />
        <PrivateRoute
          path={PROPOSAL_CREATE}
          component={ProposalCreate}
          exact
          permissions={[CAN_EDIT_CAMPAIGNS]}
        />
        <PrivateRoute
          path={PROPOSAL_CREATE_FROM_CAMPAIGN}
          component={ProposalCreate}
          permissions={[CAN_EDIT_CAMPAIGNS]}
        />
        {/** CounterOffer */}
        <PrivateRoute
          path={PROPOSAL_PRODUCT_COUNTEROFFER_CAT}
          component={ProposalCounterOffer}
          permissions={[CAN_EDIT_PRODUCTS]}
        />
        <PrivateRoute
          path={PROPOSAL_COMMITTED}
          component={ProposalCommitted}
          permissions={[CAN_VIEW_PROPOSALS]}
        />
        {/* products */}
        <PrivateRoute
          path={PRODUCT_LIST}
          permissions={[CAN_EDIT_PRODUCTS]}
          exact
          component={ProductList}
        />
        <PrivateRoute
          path={PRODUCT_CREATE}
          permissions={[CAN_EDIT_PRODUCTS]}
          exact
          component={ProductCreate}
        />
        <PrivateRoute
          path={PRODUCT_EDIT}
          permissions={[CAN_EDIT_PRODUCTS]}
          component={ProductEdit}
        />
        {/* market */}
        <PrivateRoute
          path={MARKET_SHOW}
          permissions={[CAN_EDIT_CAMPAIGNS]}
          component={MarketList}
        />
        {/* users */}
        <PrivateRoute
          path={USER_LIST}
          permissions={[CAN_VIEW_USERS]}
          exact
          component={UserList}
        />
        <PrivateRoute path={USER_SETTINGS} exact component={UserSettings} />
        {/* <PrivateRoute
          path={USER_CREATE}
          permissions={[CAN_EDIT_USERS]}
          exact
          component={UserCreate}
        />
        <PrivateRoute
          path={USER_SHOW}
          permissions={[CAN_VIEW_USERS]}
          exact
          component={UserShow}
        />
        <PrivateRoute
          path={USER_EDIT}
          permissions={[CAN_EDIT_USERS]}
          component={UserEdit}
        /> */}
        {/* ad formats */}
        <PrivateRoute
          path={AD_FORMAT_LIST}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdFormatList}
        />
        <PrivateRoute
          path={AD_FORMAT_CREATE}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdFormatCreate}
        />
        <PrivateRoute
          path={AD_FORMAT_EDIT}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdFormatEdit}
        />
        {/* ad slots */}
        <PrivateRoute
          path={AD_SLOT_LIST}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdSlotList}
        />
        <PrivateRoute
          path={AD_SLOT_CREATE}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdSlotCreate}
        />
        <PrivateRoute
          path={AD_SLOT_EDIT}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdSlotEdit}
        />
        {/* ad types */}
        <PrivateRoute
          path={AD_TYPE_LIST}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          component={AdTypeList}
        />
        {/* Trading */}
        <PrivateRoute
          path={TRADING_LIST}
          permissions={[CAN_GENERATE_TRADING_REPORT]}
          component={Trading}
        />
        {/* client manager */}
        <PrivateRoute
          path={CLIENT_MANAGER_LIST}
          permissions={[CAN_VIEW_CLIENT_MANAGER]}
          component={ClientManagerList}
        />
        <PrivateRoute
          path={CLIENT_MANAGER_CREATE}
          permissions={[CAN_EDIT_CLIENT_MANAGER]}
          component={ClientManagerCreate}
        />
        <PrivateRoute
          path={CLIENT_MANAGER_EDIT}
          permissions={[CAN_EDIT_CLIENT_MANAGER]}
          component={ClientManagerEdit}
        />
        <Route path='*'>
          <NotFound />
        </Route>
      </Switch>
    </Security>
  );
};

export default Router;
